import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useProjectRepos } from '@/hooks/use-projects';
import { formatRelativeTime } from '@/utils/format-time';

import type { ProjectRepository } from '@/types/project';

function RepositoryItem({ repo, projectKey }: { repo: ProjectRepository; projectKey: string }) {
  return (
    <li>
      <Link
        to={`/projects/${encodeURIComponent(projectKey)}/repos/${encodeURIComponent(repo.name)}`}
        className="block rounded-lg border border-border p-4 hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-content">{repo.name}</span>
          {repo.openPrCount > 0 && (
            <span
              className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-200"
              aria-label={`${repo.openPrCount} open pull request${repo.openPrCount !== 1 ? 's' : ''}`}
            >
              {repo.openPrCount} open PR{repo.openPrCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-content-secondary line-clamp-2">
          {repo.description || 'No description'}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-content-tertiary">
          <span className="inline-flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {repo.defaultBranch || 'main'}
          </span>
          <span className="inline-flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {repo.lastModifiedDate ? formatRelativeTime(repo.lastModifiedDate) : 'Unknown'}
          </span>
        </div>
      </Link>
    </li>
  );
}

function RepoListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading repositories" className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-lg border border-border p-4">
          <div className="flex items-center justify-between">
            <div className="h-4 w-48 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProjectReviewPage() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';
  const [repoSearch, setRepoSearch] = useState('');

  const {
    data: repos,
    isLoading: reposLoading,
    isError: reposError,
    refetch: refetchRepos,
  } = useProjectRepos(key);

  const filteredRepos = useMemo(() => {
    if (!repos) return [];
    const trimmed = repoSearch.trim().toLowerCase();
    if (trimmed.length === 0) return repos;
    return repos.filter(
      (repo) =>
        repo.name.toLowerCase().includes(trimmed) ||
        (repo.description?.toLowerCase().includes(trimmed) ?? false),
    );
  }, [repos, repoSearch]);

  return (
    <main id="main-content" className="mx-auto max-w-4xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-content">Code Review</h1>
        <p className="mt-1 text-sm text-content-secondary">
          Repositories and open pull requests
        </p>
      </header>

      {reposLoading && <RepoListSkeleton />}
      {reposError && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load repositories.</p>
          <button
            onClick={() => refetchRepos()}
            className="mt-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      )}
      {!reposLoading && !reposError && repos && (
        <>
          {repos.length === 0 ? (
            <div className="rounded-lg border border-border p-6 text-center">
              <p className="text-sm text-content-secondary">No repositories found for this project.</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label htmlFor="repo-search" className="sr-only">Search repositories</label>
                <input
                  id="repo-search"
                  type="search"
                  placeholder="Search repositories..."
                  value={repoSearch}
                  onChange={(e) => setRepoSearch(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-content placeholder-content-tertiary text-sm focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-transparent"
                  aria-label="Search repositories by name or description"
                />
              </div>
              {filteredRepos.length === 0 ? (
                <div className="rounded-lg border border-border p-6 text-center">
                  <p className="text-sm text-content-secondary">
                    No repositories match &ldquo;{repoSearch}&rdquo;
                  </p>
                </div>
              ) : (
                <ul className="space-y-2" aria-label="Project repositories">
                  {filteredRepos.map((repo) => (
                    <RepositoryItem key={repo.name} repo={repo} projectKey={key} />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </main>
  );
}
